import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Form, Input, Select, Button, Typography, Spin, Space, Upload, InputNumber, Row, Col, Badge } from "antd";
import { SaveOutlined, ReloadOutlined, ArrowLeftOutlined, UploadOutlined, VideoCameraOutlined, EyeOutlined, SortAscendingOutlined, PictureOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { getImageUrl } from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Option } = Select;

const EditSubGenre = () => {
  const { genre_id } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [languages, setLanguages] = useState([]);
  const [genres, setGenres] = useState([]);
  const [multilingualData, setMultilingualData] = useState([]);
  const notify = useNotify();

  const fetchGenreDetails = useCallback(async () => {
    if (!genre_id) {
      notify.error({ message: "Genre ID is required" });
      navigate("/sub-genres");
      return;
    }

    setLoadingData(true);
    try {
      const response = await masterService.getSingleSubGenre(genre_id);

      if (response.data && response.data.status) {
        const genreData = response.data.data[0] || response.data.data;
        if (genreData && genreData.genre_name) {
          form.setFieldsValue({
            genre_name: genreData.genre_name,
            genre_is_active: genreData.genre_is_active || "Y",
            show_in_preference: genreData.show_in_preference || "N",
            preference_order_number: genreData.preference_order_number || 0,
            image_url: genreData.image_url,
            logo_url: genreData.logo_url,
            genre_description: genreData.genre_description || "",
            genre_keywords: genreData.genre_keywords
              ? String(genreData.genre_keywords)
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : [],
            fk_parent_genre_id: genreData.fk_parent_genre_id || null,
          });
          // Set state variables for image previews
          setImageUrl(genreData.image_url || "");
          setLogoUrl(genreData.logo_url || "");
          // Prefill multilingual data if available
          if (Array.isArray(genreData.multilingual_data)) {
            setMultilingualData(
              genreData.multilingual_data.map((d) => ({
                lang_id: d.lang_id ?? null,
                genre_name: d.genre_name ?? "",
                genre_description: d.genre_description ?? "",
                genre_keywords: d.genre_keywords
                  ? String(d.genre_keywords)
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : [],
              }))
            );
          }
        } else {
          notify.error({ message: "Genre data not found" });
          navigate("/sub-genres");
        }
      } else {
        notify.error({
          message: response.data?.message || "Failed to fetch genre details",
        });
        navigate("/sub-genres");
      }
    } catch (error) {
      console.error("Error fetching genre details:", error);
      notify.error({ message: "Error fetching genre details" });
      navigate("/sub-genres");
    } finally {
      setLoadingData(false);
    }
  }, [genre_id, form, navigate]);

  useEffect(() => {
    setPageTitle("Edit Sub Genre");
    fetchGenreDetails();
  }, [fetchGenreDetails, setPageTitle]);

  // Load languages
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const params = {
          perPage: 1000,
          currentPage: 1,
          searchText: "",
          status: "Y",
        };
        const res = await masterService.getVideoLanguages(params);
        const list =
          res?.data?.data?.data || res?.data?.data || res?.data || [];
        setLanguages(list);
      } catch (e) {
        notify.error({ message: "Failed to load languages" });
      }
    };
    const loadGenres = async () => {
      try {
        const res = await masterService.getGenres({ isAll: true });
        console.log("res123", res);
        const list =
          res?.data?.data?.data || res?.data?.data || res?.data || [];
        setGenres(list);
      } catch (e) {
        notify.error({ message: "Failed to load genres" });
      }
    };
    loadLanguages();
    loadGenres();
  }, []);

  const onImageUpload = async (file, key) => {
    try {
      const formData = new FormData();
      formData.append("image_type", "images");
      formData.append("folder_name", "genre");
      formData.append("image", file);
      const response = await masterService.uploadImage(formData);
      console.log("response.data.data.s3_key", response.data.data.s3_key);
      const uploadedUrl = response.data.data.s3_key;
      form.setFieldValue(key, uploadedUrl);

      // Update state variables to trigger re-render
      if (key === "image_url") {
        setImageUrl(uploadedUrl);
      } else if (key === "logo_url") {
        setLogoUrl(uploadedUrl);
      }

      notify.success({ message: "Image uploaded successfully" });
    } catch (e) {
      notify.error({ message: "Failed to upload image" });
    }
  };

  // Multilingual helpers
  const addMultilingualData = () => {
    setMultilingualData([
      ...multilingualData,
      {
        lang_id: null,
        genre_name: "",
        genre_description: "",
        genre_keywords: [],
      },
    ]);
  };
  const handleMultilingualDataRemove = (index) => {
    setMultilingualData(multilingualData.filter((_, i) => i !== index));
  };

  const handleSubmit = async (values) => {
    // Validate multilingual data: no duplicate languages and require name if language is selected
    const langs = multilingualData.map((d) => d.lang_id).filter(Boolean);
    if (new Set(langs).size !== langs.length) {
      notify.error({ message: "Duplicate languages in multilingual data" });
      return;
    }
    for (let i = 0; i < multilingualData.length; i++) {
      const d = multilingualData[i];
      if (d.lang_id && !d.genre_name) {
        notify.error({
          message: `Language entry #${
            i + 1
          }: Genre name is required when language is selected`,
        });
        return;
      }
    }
    setLoading(true);
    try {
      const parentKeywords = Array.isArray(values.genre_keywords)
        ? values.genre_keywords
            .map((s) => String(s).trim())
            .filter(Boolean)
            .join(",")
        : values.genre_keywords || "";
      const multilingualSerialized = (multilingualData || []).map((d) => ({
        ...d,
        genre_keywords: Array.isArray(d.genre_keywords)
          ? d.genre_keywords
              .map((s) => String(s).trim())
              .filter(Boolean)
              .join(",")
          : d.genre_keywords || "",
      }));
      const payload = {
        ...values,
        genre_id: genre_id,
        genre_keywords: parentKeywords,
        multilingual_data: multilingualSerialized,
      };

      const response = await masterService.updateSubGenre(genre_id, payload);

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Genre updated successfully",
        });
        navigate("/sub-genres");
      } else {
        notify.error({
          message: response.data.message || "Failed to update genre",
        });
      }
    } catch (error) {
      console.error("Error updating genre:", error);
      notify.error({ message: "Error updating genre" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/genres");
  };

  return (
    <div className="min-h-screen">
      <div className="">
        <Spin spinning={loadingData}>
          <Card>
            <Form
              form={form}
              layout="vertical"
              size="large"
              onFinish={handleSubmit}
              className="space-y-6"
            >
              {/* Basic Information Section */}
              <div
                className="bg-white p-6 rounded-lg border border-gray-200"
                style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <VideoCameraOutlined />
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Basic Information
                  </Title>
                </div>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Genre Name
                        </span>
                      }
                      name="genre_name"
                      rules={[
                        { required: true, message: "Please enter genre name" },
                      ]}
                    >
                      <Input
                        placeholder="Enter genre name"
                        className="rounded-lg"
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Status
                        </span>
                      }
                      name="genre_is_active"
                    >
                      <Select
                        placeholder="Select status"
                        className="rounded-lg"
                      >
                        <Option value="Y">
                          <Badge status="success" text="Active" />
                        </Option>
                        <Option value="N">
                          <Badge status="default" text="Inactive" />
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Parent Genre
                        </span>
                      }
                      name="fk_parent_genre_id"
                    >
                      <Select
                        placeholder="Select parent genre"
                        className="rounded-lg"
                        showSearch
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {genres.map((genre) => (
                          <Option key={genre.genre_id} value={genre.genre_id}>
                            {genre.genre_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Description
                        </span>
                      }
                      name="genre_description"
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="Enter description"
                        className="rounded-lg"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Keywords
                        </span>
                      }
                      name="genre_keywords"
                    >
                      <Select
                        mode="tags"
                        placeholder="Type a keyword and press Enter"
                        tokenSeparators={[","]}
                        open={false}
                        className="rounded-lg"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Preference Settings Section */}
              <div
                className="bg-white p-6 rounded-lg border border-gray-200"
                style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <EyeOutlined />
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Preference Settings
                  </Title>
                </div>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Show in Preference
                        </span>
                      }
                      name="show_in_preference"
                    >
                      <Select
                        placeholder="Select preference visibility"
                        className="rounded-lg"
                      >
                        <Option value="Y">
                          <Badge status="success" text="Yes" />
                        </Option>
                        <Option value="N">
                          <Badge status="default" text="No" />
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Preference Order
                        </span>
                      }
                      name="preference_order_number"
                      rules={[
                        {
                          pattern: /^(0|[1-9]\d*)$/,
                          message:
                            "Preference order number must be a non-negative integer",
                        },
                      ]}
                    >
                      <InputNumber
                        placeholder="Enter preference order number"
                        style={{ width: "100%" }}
                        className="rounded-lg"
                        addonBefore={<SortAscendingOutlined />}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Media Assets Section */}
              <div
                className="bg-white p-6 rounded-lg border border-gray-200"
                style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <PictureOutlined />
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Media Assets
                  </Title>
                </div>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="image_url"
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Genre Image
                        </span>
                      }
                    >
                      {imageUrl && (
                        <div className="mb-4">
                          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3">
                            <Text strong className="text-orange-700 text-sm">
                              {getImageUrl(imageUrl)}
                            </Text>
                          </div>
                          <div className="w-full h-32 border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                            <img
                              src={getImageUrl(imageUrl)}
                              alt="Genre preview"
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                        </div>
                      )}

                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={(f) => {
                          onImageUpload(f, "image_url");
                          return false;
                        }}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          className="w-full rounded-lg"
                          type={imageUrl ? "default" : "primary"}
                        >
                          {imageUrl
                            ? "Change Genre Image"
                            : "Upload Genre Image"}
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="logo_url"
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Genre Logo
                        </span>
                      }
                    >
                      {logoUrl && (
                        <div className="mb-4">
                          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3">
                            <Text strong className="text-orange-700 text-sm">
                              {getImageUrl(logoUrl)}
                            </Text>
                          </div>
                          <div className="w-full h-32 border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                            <img
                              src={getImageUrl(logoUrl)}
                              alt="Logo preview"
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                        </div>
                      )}

                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={(f) => {
                          onImageUpload(f, "logo_url");
                          return false;
                        }}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          className="w-full rounded-lg"
                          type={logoUrl ? "default" : "primary"}
                        >
                          {logoUrl ? "Change Genre Logo" : "Upload Genre Logo"}
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Multilingual Data Section */}
              <div
                className="bg-white p-6 rounded-lg border border-gray-200"
                style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center justify-between mb-4">
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Multilingual Data
                  </Title>
                  <Button
                    type="dashed"
                    onClick={addMultilingualData}
                    icon={<UploadOutlined />}
                  >
                    Add Language
                  </Button>
                </div>

                {multilingualData.length === 0 && (
                  <Card style={{ textAlign: "center", background: "#fafafa" }}>
                    <Text type="secondary">
                      No multilingual data added yet. Click "Add Language" to
                      get started.
                    </Text>
                  </Card>
                )}

                <div className="space-y-4">
                  {multilingualData.map((data, index) => (
                    <Card
                      key={`ml-${index}`}
                      title={
                        <div className="flex items-center justify-between">
                          <Text strong>
                            Language Entry #{index + 1}
                            {data.lang_id &&
                              ` - ${
                                languages.find(
                                  (l) => l.lang_id === data.lang_id
                                )?.lang_name || "Unknown Language"
                              }`}
                          </Text>
                          <Button
                            type="link"
                            danger
                            onClick={() => handleMultilingualDataRemove(index)}
                          >
                            Remove
                          </Button>
                        </div>
                      }
                    >
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="Language"
                            required
                            style={{ marginBottom: 0 }}
                          >
                            <Select
                              value={data.lang_id}
                              placeholder="Select language"
                              onChange={(value) => {
                                const arr = [...multilingualData];
                                arr[index].lang_id = value;
                                setMultilingualData(arr);
                              }}
                              showSearch
                              filterOption={(input, option) =>
                                option.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                            >
                              {languages.map((lang) => (
                                <Option key={lang.lang_id} value={lang.lang_id}>
                                  {lang.lang_name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="Sub Genre Name"
                            required
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              value={data.genre_name}
                              placeholder="Enter sub genre name in this language"
                              onChange={(e) => {
                                const arr = [...multilingualData];
                                arr[index].genre_name = e.target.value;
                                setMultilingualData(arr);
                              }}
                              onBlur={(e) => {
                                const arr = [...multilingualData];
                                arr[index].genre_name = e.target.value.trim();
                                setMultilingualData(arr);
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="Sub Genre Description"
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea
                              rows={3}
                              value={data.genre_description}
                              placeholder="Enter sub genre description in this language"
                              onChange={(e) => {
                                const arr = [...multilingualData];
                                arr[index].genre_description = e.target.value;
                                setMultilingualData(arr);
                              }}
                              onBlur={(e) => {
                                const arr = [...multilingualData];
                                arr[index].genre_description = e.target.value.trim();
                                setMultilingualData(arr);
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="Sub Genre Keywords"
                            style={{ marginBottom: 0 }}
                          >
                            <Select
                              mode="tags"
                              value={
                                Array.isArray(data.genre_keywords)
                                  ? data.genre_keywords
                                  : data.genre_keywords
                                  ? String(data.genre_keywords)
                                      .split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean)
                                  : []
                              }
                              placeholder="Type a sub genre keyword and press Enter"
                              onChange={(value) => {
                                const arr = [...multilingualData];
                                arr[index].genre_keywords = value;
                                setMultilingualData(arr);
                              }}
                              tokenSeparators={[","]}
                              open={false}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div
                className="flex justify-end gap-4 pt-6 border-t"
                style={{ borderColor: "#d9d9d9" }}
              >
                <Space size="middle">
                  <Button
                    onClick={handleReset}
                    icon={<ReloadOutlined />}
                    size="large"
                    className="px-6"
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={handleGoBack}
                    icon={<ArrowLeftOutlined />}
                    size="large"
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<SaveOutlined />}
                    size="large"
                    className="px-8"
                  >
                    {loading ? "Updating..." : "Update Sub Genre"}
                  </Button>
                </Space>
              </div>
            </Form>
          </Card>
        </Spin>
      </div>
    </div>
  );
};

export default EditSubGenre;
